import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { 
  Plus, 
  Search, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  RefreshCw,
  Filter,
  MoreHorizontal,
  PackageOpen,
  Users,
  BadgeCheck,
  PieChart,
  ChevronDown,
  Layers,
  TrendingUp,
  Activity,
  Star,
  Grid3X3,
  Download,
  Settings
} from 'lucide-react';
import SubscriptionPlanFamiliesTable from '@/components/subscription-plans/SubscriptionPlanFamiliesTable';
import PageContainer from '@/components/common/PageContainer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';

// Interface for subscription plan family
interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description?: string;
  target?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export default function SubscriptionPlanFamilies() {
  // Dialog states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [target, setTarget] = useState('');
  const [isActive, setIsActive] = useState(true);
  
  // Data and loading states
  const [loading, setLoading] = useState(true);
  const [families, setFamilies] = useState<SubscriptionPlanFamily[]>([]);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const { toast } = useToast();

  // Fetch subscription plan families
  const fetchFamilies = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setFamilies(data || []);
    } catch (error) {
      console.error('Error fetching families:', error);
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar famílias',
        description: 'Não foi possível carregar as famílias de planos.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch data on mount only
  useEffect(() => {
    fetchFamilies();
  }, []);

  // Simple filtering function like CourseFamilies.tsx
  const getFilteredFamilies = () => {
    let filtered = families;

    // Apply text search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(family => 
        family.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        family.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        family.target?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(family => 
        statusFilter === 'active' ? family.is_active : !family.is_active
      );
    }

    return filtered;
  };

  const filteredFamilies = getFilteredFamilies();
  const totalPages = Math.ceil(filteredFamilies.length / pageSize);

  // Pagination handlers
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  // Simple pagination calculation like CourseFamilies.tsx
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredFamilies.slice(startIndex, endIndex);
  };

  const currentPageItems = getCurrentPageItems();

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase
        .from('subscription_plan_families')
        .insert({
          name,
          description,
          target,
          is_active: isActive,
        });

      if (error) throw error;

      toast({
        title: 'Família criada',
        description: 'A família de planos foi criada com sucesso.',
      });

      setIsModalOpen(false);
      resetForm();
      fetchFamilies(); // Atualiza a tabela após criar a família
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar família',
        description: 'Não foi possível criar a família de planos. Por favor, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setTarget('');
    setIsActive(true);
  };



  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Famílias de Planos</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Header with Gradient Background */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path d="M0 0h826.54v502.06H0z" fill="none"/>
                <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
                <defs>
                  <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#6366f1" stopOpacity=".1"/>
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity=".1"/>
                  </linearGradient>
                </defs>
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-6 pb-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Layers className="h-6 w-6 text-white" />
                  </div>
          <div>
                    <h1 className="text-2xl font-extrabold text-white">Famílias de Planos</h1>
                    <p className="text-blue-100 text-base pb-2">
                      Gerencie e organize suas famílias de planos de assinatura
            </p>
          </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={() => fetchFamilies()} 
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Atualizar
                </Button>
            <Button 
                  size="default" 
              onClick={() => setIsModalOpen(true)}
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
            >
                  <Plus className="h-4 w-4 mr-2" />
              Nova Família
            </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10">
          {/* Enhanced Summary Cards with Better Visual Hierarchy */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
            <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 rounded-md">
                      <Layers className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Total de Famílias</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {families.length}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Categorias disponíveis
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-green-600 font-extrabold">+12%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-100 rounded-md">
                      <BadgeCheck className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Famílias Ativas</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {families.filter(family => family.is_active).length}
                  </div>
                </div>
                <div className="space-y-2">
                  <Progress 
                    value={families.length > 0 ? (families.filter(family => family.is_active).length / families.length) * 100 : 0} 
                    className="h-1.5"
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {families.length > 0 
                        ? `${Math.round((families.filter(family => family.is_active).length / families.length) * 100)}% do total` 
                        : 'Nenhuma família'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Activity className="h-3 w-3 text-green-500" />
                      <span className="text-xs text-green-600 font-extrabold">+5%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-red-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
          <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-red-100 rounded-md">
                      <X className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Famílias Inativas</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {families.filter(family => !family.is_active).length}
                  </div>
                </div>
                <div className="space-y-2">
                  <Progress 
                    value={families.length > 0 ? (families.filter(family => !family.is_active).length / families.length) * 100 : 0} 
                    className="h-1.5"
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {families.length > 0 
                        ? `${Math.round((families.filter(family => !family.is_active).length / families.length) * 100)}% do total` 
                        : 'Nenhuma família'}
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-red-500 rotate-180" />
                      <span className="text-xs text-red-600 font-extrabold">-2%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Enhanced Main Content with Modern Design */}
          <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              {/* Enhanced Filter Section */}
              <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4 flex-1">
                  <div className="relative min-w-0 flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 text-gray-400 transform -translate-y-1/2" />
                <Input
                      placeholder="Pesquisar famílias..."
                  value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                />
                {searchTerm && (
                  <button
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={clearSearch}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-10 px-4 border-gray-200 hover:bg-gray-50">
                        <Filter className="h-4 w-4 mr-2" />
                        Filtros
                        {statusFilter !== 'all' && (
                          <Badge variant="secondary" className="ml-2 h-4 w-4 p-0 text-xs">
                            1
                          </Badge>
                        )}
                        <ChevronDown className="h-3 w-3 ml-2 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-6" align="end">
                      <div className="space-y-6">
                        <div>
                          <h4 className="font-extrabold text-gray-900 mb-1">Filtros Avançados</h4>
                          <p className="text-sm text-gray-500">Personalize a visualização das famílias</p>
                        </div>
                        
                        <Separator />
                        
                        <div className="space-y-4">
              <div>
                            <label className="text-sm font-extrabold text-gray-700 mb-2 block">
                              Status da Família
                            </label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                              <SelectTrigger className="w-full">
                                <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                  </SelectContent>
                </Select>
                          </div>
              </div>
              
                        <div className="flex justify-between pt-4 border-t">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setSearchTerm('');
                              setStatusFilter('all');
                            }}
                          >
                            Limpar Filtros
                </Button>
              </div>
            </div>
                    </PopoverContent>
                  </Popover>
            </div>
              
                <div className="flex items-center gap-2">
                  <Select
                    value={pageSize.toString()}
                    onValueChange={handlePageSizeChange}
                  >
                    <SelectTrigger className="w-[120px] h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 por página</SelectItem>
                      <SelectItem value="20">20 por página</SelectItem>
                      <SelectItem value="50">50 por página</SelectItem>
                      <SelectItem value="100">100 por página</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Content Area */}
              {loading ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg">
                      <Skeleton className="h-12 w-12 rounded-full" />
                      <div className="space-y-2 flex-1">
                        <Skeleton className="h-5 w-40" />
                        <Skeleton className="h-4 w-60" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-6 w-16" />
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredFamilies.length === 0 ? (
                <div className="text-center py-20">
                  <div className="space-y-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                      <Layers className="h-12 w-12 text-blue-500" />
                    </div>
                    <div className="space-y-3">
                      <h3 className="text-xl font-extrabold text-gray-900">
                        Nenhuma família encontrada
                      </h3>
                      <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                        {searchTerm || statusFilter !== 'all' 
                          ? 'Não encontramos famílias que correspondam aos critérios de busca.'
                          : 'Crie sua primeira família de planos para organizar e categorizar seus diferentes tipos de assinaturas.'
                        }
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      {searchTerm || statusFilter !== 'all' ? (
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setSearchTerm('');
                            setStatusFilter('all');
                          }}
                          size="lg"
                        >
                          <X className="h-5 w-5 mr-2" />
                          Limpar Filtros
                        </Button>
                      ) : (
                        <Button onClick={() => setIsModalOpen(true)} size="lg">
                          <Plus className="h-5 w-5 mr-2" />
                          Criar Primeira Família
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">

                  <SubscriptionPlanFamiliesTable 
                    data={currentPageItems}
                    onRefresh={fetchFamilies}
                    bare={true}
                  />
                  
                  {/* Modern Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <span>
                          Exibindo <span className="font-extrabold text-gray-900">{Math.min((currentPage - 1) * pageSize + 1, filteredFamilies.length)}</span> até{" "}
                          <span className="font-extrabold text-gray-900">{Math.min(currentPage * pageSize, filteredFamilies.length)}</span> de{" "}
                          <span className="font-extrabold text-gray-900">{filteredFamilies.length}</span> resultados
                        </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                          size="sm"
                    onClick={previousPage}
                    disabled={currentPage === 1}
                          className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                            const pageNumber = i + 1;
                            return (
                              <Button
                                key={pageNumber}
                                variant={currentPage === pageNumber ? "default" : "outline"}
                                size="sm"
                                onClick={() => setCurrentPage(pageNumber)}
                                className="h-8 w-8 p-0"
                              >
                                {pageNumber}
                              </Button>
                            );
                          })}
                  </div>
                  <Button
                    variant="outline"
                          size="sm"
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                          className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
                  )}
                </div>
          )}
            </CardContent>
          </Card>
        </div>
                
        {/* Bottom Spacing */}
        <div className="h-16" />
        </div>

        {/* Create Family Dialog */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px] bg-white/95 backdrop-blur-sm border-0 shadow-2xl">
          <DialogHeader className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl font-extrabold text-gray-900">Nova Família de Planos</DialogTitle>
                <DialogDescription className="text-gray-600">
                  Configure os detalhes da nova família de planos de assinatura
              </DialogDescription>
              </div>
            </div>
            </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-extrabold text-gray-700">
                  Nome da Família <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Planos Enterprise"
                  required
                  className={`bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 ${
                    !name.trim() ? "border-red-300 focus:border-red-500 focus:ring-red-500" : ""
                  }`}
                />
                {!name.trim() && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <X className="h-3 w-3" />
                    Nome é obrigatório
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-extrabold text-gray-700">
                  Descrição
                </Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descreva o propósito desta família de planos..."
                  rows={4}
                  className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                />
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Settings className="h-3 w-3" />
                  Uma boa descrição ajuda a entender o objetivo desta família de planos
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target" className="text-sm font-extrabold text-gray-700">
                  Público-Alvo
                </Label>
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger className="bg-white/50 border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                    <SelectValue placeholder="Selecione o público-alvo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2B">B2B - Business to Business</SelectItem>
                    <SelectItem value="B2C">B2C - Business to Consumer</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Selecione o modelo de negócio desta família de planos
                </p>
              </div>

              <div className="flex items-center space-x-3 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200/50">
                <Checkbox
                    id="is_active"
                    checked={isActive}
                  onCheckedChange={(checked) => setIsActive(!!checked)}
                  className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                  />
                <Label htmlFor="is_active" className="text-sm font-extrabold text-gray-700 cursor-pointer flex items-center gap-2">
                  <BadgeCheck className="h-4 w-4 text-green-600" />
                  Família ativa ao criar
                </Label>
              </div>
              </div>

            <DialogFooter className="flex gap-3 pt-6 border-t border-gray-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetForm();
                    setIsModalOpen(false);
                  }}
                  disabled={loading}
                className="flex-1 border-gray-200 hover:bg-gray-50"
                >
                <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading || !name.trim()}
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg transition-all duration-300"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      Criando...
                    </>
                  ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Família
                  </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
    </>
  );
}